import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { KpiStrip, KpiTileLoading, type KpiItem } from '../components/KpiTile';
import { SkeletonRow } from '../components/SkeletonRow';
import { EmptyState } from '../components/EmptyState';
import { getEngagementHealth } from '../lib/api';
import { EngagementScopeFilters } from '../components/EngagementScopeFilters';
import { ACTIVE_ENGAGEMENT_SCOPE, type EngagementScope } from '../../../src/reports/shared/engagement-scope';
import { affiliateDrilldownUrl, rollupAffiliateAlerts } from '../lib/affiliate-rollup';

/**
 * Affiliates roll-up: which delivering affiliates are showing up in the most
 * engagement-health alerts. Clicking a row drills into the Engagement Health
 * page filtered to that affiliate (the "who are this week's three or four
 * offenders" view Danielle asked for on 2026-05-20).
 */
export function Affiliates(): JSX.Element {
  const navigate = useNavigate();
  const [scope, setScope] = useState<EngagementScope>(ACTIVE_ENGAGEMENT_SCOPE);
  const { data: health, isLoading } = useQuery({ queryKey: ['engagement-health'], queryFn: getEngagementHealth });
  const affiliates = useMemo(() => (health ? rollupAffiliateAlerts(health.alerts, scope) : undefined), [health, scope]);

  const rows = affiliates ?? [];

  const kpis = useMemo<KpiItem[]>(() => {
    if (!affiliates || affiliates.length === 0) return [];
    const totalAlerts = affiliates.reduce((s, a) => s + a.total, 0);
    const overOne = affiliates.filter((a) => a.total >= 2).length;
    const top = affiliates[0];
    return [
      { label: 'Affiliates with alerts', value: affiliates.length, sub: 'distinct coaches' },
      { label: 'Repeat offenders', value: overOne, sub: '2+ alerts on the same affiliate' },
      { label: 'Top affiliate', value: top?.name ?? '—', sub: top ? `${top.total} alerts` : '' },
      { label: 'Total alert rows', value: totalAlerts, sub: 'across all affiliates' },
    ];
  }, [affiliates]);

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd">
          <div>
            <div className="eb">Affiliates</div>
            <h1 className="d-title">Coaches with active alerts</h1>
            <div className="d-meta" style={{ marginTop: 8, fontSize: 13 }}>
              Click a coach to filter the Engagement Health view to just their engagements.
            </div>
          </div>
        </div>

        <EngagementScopeFilters value={scope} onChange={setScope}
          accounts={(health?.engagements ?? []).map((e) => e.accountDisplayName || e.accountName || '').filter(Boolean)}
          statuses={(health?.engagements ?? []).map((e) => e.engagementStatus?.trim() || '(blank)')} />

        {isLoading ? (
          <div className="kpi-strip">{[0, 1, 2, 3].map((i) => <KpiTileLoading key={i} />)}</div>
        ) : kpis.length ? (
          <KpiStrip items={kpis} />
        ) : null}

        <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
          {isLoading ? (
            <table className="tbl gen">
              <thead>
                <tr><th>Affiliate</th><th className="num">Stalled</th><th className="num">Near complete</th><th className="num">Stale</th><th className="num">Total</th><th /></tr>
              </thead>
              <tbody>{Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cells={6} />)}</tbody>
            </table>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="check"
              title="No affiliates with active alerts"
              body="Adjust the status/account filters, or refresh Engagement Health to populate this view."
            />
          ) : (
            <table className="tbl gen">
              <thead>
                <tr>
                  <th>Affiliate</th>
                  <th>Email</th>
                  <th className="num">Stalled</th>
                  <th className="num">Near completion</th>
                  <th className="num">Stale</th>
                  <th className="num">No session 30d</th>
                  <th className="num">Total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr
                    key={a.name}
                    onClick={() => navigate(affiliateDrilldownUrl(a.name, scope))}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{a.name}</strong></td>
                    <td style={{ color: 'var(--mute)', fontSize: 13 }}>{a.email ?? <span className="em">—</span>}</td>
                    <td className="num">{a.stalledStart || ''}</td>
                    <td className="num">{a.nearCompletion || ''}</td>
                    <td className="num">{a.staleActivity || ''}</td>
                    <td className="num">{a.staleActivity30d || ''}</td>
                    <td className="num"><strong>{a.total}</strong></td>
                    <td style={{ paddingRight: 16, color: 'var(--mute)' }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
